import { IconCash, IconCreditCard, IconPigMoney } from '@tabler/icons-react';

/**
 * Active-ledger grouping and the "Last bill paid" lookup, shared by the
 * Ledgers page and the Dashboard's ledger groups (#1971) so a group has the
 * same name, order and members on both.
 */

// Display order + labels for the active-ledger groups (#907). Order is
// intentional, not numeric: Checking, then Credit Cards, then Savings. Each
// group carries its v3 category (icon-box tint + dot color) and Tabler glyph.
export const LEDGER_GROUPS = [
  { type: 0, label: 'Checking', category: 'checking', Icon: IconCash },
  { type: 2, label: 'Credit Cards', category: 'credit', Icon: IconCreditCard },
  { type: 1, label: 'Savings', category: 'savings', Icon: IconPigMoney },
];

/** Every ledger type but Checking shows "Last bill paid". */
export const showsLastBillPaid = (ledger) => ledger.type !== 0;

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The lines request behind a ledger's "Last bill paid": the last 35 days in
 * local time, newest first. calculateLastBillPaid keeps the last 30 of them.
 * @param {string} ledgerId
 * @param {Date} [now]
 * @returns {string} API path
 */
export const lastBillLinesPath = (ledgerId, now = new Date()) => {
  const from = new Date(now);
  from.setDate(from.getDate() - 35);
  return `/lines?ledgerId=${encodeURIComponent(ledgerId)}&from=${ymd(from)}&to=${ymd(now)}&order=desc`;
};

/**
 * Lowercased names of the workspace's Checking ledgers. A Checking ledger's
 * linked payee carries its name (#1944), so a card line with that payee name
 * is a payment from Checking.
 * @param {Array} ledgers
 * @returns {Set<string>}
 */
export const checkingLedgerNames = (ledgers) => new Set(
  (ledgers || []).filter((l) => l.type === 0).map((l) => (l.name || '').toLowerCase())
);

const byDateDesc = (a, b) => {
  const dateA = a.dateStamp ? new Date(a.dateStamp + 'T00:00:00').getTime() : 0;
  const dateB = b.dateStamp ? new Date(b.dateStamp + 'T00:00:00').getTime() : 0;
  return dateB - dateA;
};

/**
 * The latest bill payment in the last 30 days. A line whose payee is a
 * Checking ledger's payee wins (#1944); the name heuristics below stay as
 * the fallback for history entered before ledger-linked payees.
 * @param {Array} ledgerLines
 * @param {Set<string>} [checkingNames]  from checkingLedgerNames
 */
export const calculateLastBillPaid = (ledgerLines, checkingNames = new Set()) => {
  if (!ledgerLines || ledgerLines.length === 0) return null;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recent = ledgerLines.filter(line => {
    const lineDate = line.dateStamp ? new Date(line.dateStamp + 'T00:00:00') : null;
    return Boolean(lineDate) && lineDate >= thirtyDaysAgo;
  });

  const fromChecking = recent.filter(line => checkingNames.has((line.payeeName || '').toLowerCase()));
  if (fromChecking.length > 0) return fromChecking.slice().sort(byDateDesc)[0];

  const billPaymentLines = recent.filter(line => {
    const payeeName = (line.payeeName || '').toLowerCase();
    const amountCents = typeof line.amountCents === 'number' ? line.amountCents : 0;

    return (
      payeeName.includes('bill paid') ||
      payeeName.includes('payment') ||
      payeeName.includes('autopay') ||
      payeeName.includes('bill pay') ||
      (amountCents > 0 && !line.budgetId)
    );
  });

  if (billPaymentLines.length === 0) return null;

  return billPaymentLines.slice().sort(byDateDesc)[0] || null;
};
